// Accept HMR as per: https://vitejs.dev/guide/api-hmr.html
if (import.meta.hot) {
    import.meta.hot.accept(() => {
        console.log("HMR")
    });
}

import './css/styles.css'


// JS
//import "./js/index.js"

import Alpine from 'alpinejs'
import collapse from '@alpinejs/collapse'
import Swiper from 'swiper'
import { Navigation, Scrollbar, EffectFade, Autoplay } from 'swiper/modules'
import * as CookieConsent from 'vanilla-cookieconsent'

// === Scroll-driven motion: reveal content blocks + parallax background shapes ===
// Runs BEFORE the heavier init below (Alpine / Swiper / cookie consent) so an
// error in any of those can't stop content blocks from being revealed.
;(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // --- Reveal content-builder blocks as they enter the viewport ---
    const revealEls = document.querySelectorAll('[data-reveal]')
    if (revealEls.length) {
        if (prefersReduced || !('IntersectionObserver' in window)) {
            revealEls.forEach((el) => el.classList.add('is-visible'))
        } else {
            const observer = new IntersectionObserver(
                (entries, obs) => {
                    for (const entry of entries) {
                        if (entry.isIntersecting) {
                            entry.target.classList.add('is-visible')
                            obs.unobserve(entry.target)
                        }
                    }
                },
                // threshold 0: reveal as soon as any part enters — also covers blocks
                // taller than the viewport, which never reach a fractional ratio.
                { rootMargin: '0px 0px -10% 0px', threshold: 0 }
            )
            revealEls.forEach((el) => observer.observe(el))
        }
    }

    // --- Parallax: drift whole decoration LAYERS as one unit. Each layer's shapes
    //     move together (compositions stay intact), and the offset is locked directly
    //     to scroll position (no easing) so the shapes track scroll 1:1 like rellax —
    //     anchored at the top of the page (pristine on load), no trailing/lag.
    //     No layout reads — `transform` composes with the shapes' own translate/rotate. ---
    // Fraction of scroll the background lags by. Tune here (one place); a layer
    // may override with data-parallax-layer="0.2".
    const PARALLAX_SPEED = 0.25
    const layers = Array.from(document.querySelectorAll('[data-parallax-layer]')).map((el) => {
        const override = parseFloat(el.dataset.parallaxLayer)
        return { el, speed: Number.isFinite(override) ? override : PARALLAX_SPEED }
    })
    if (layers.length && !prefersReduced) {
        let raf = null

        const render = () => {
            const y = window.scrollY
            for (const { el, speed } of layers) {
                el.style.transform = `translate3d(0, ${(y * speed).toFixed(2)}px, 0)`
            }
            raf = null
        }

        render() // anchor on load
        window.addEventListener('scroll', () => {
            if (raf === null) raf = requestAnimationFrame(render)
        }, { passive: true })
    }

    // --- Height gate: reveal flagged decoration shapes (data-deco-min-h) only when
    //     their containing layer/section is at least that many px tall — stops
    //     bottom-anchored shapes riding up into top-anchored ones on short pages.
    //     Shapes start `visibility: hidden` in base.css. Not gated on reduced-motion:
    //     overlap is a layout problem, not a motion one. ---
    const gated = document.querySelectorAll('[data-deco-min-h]')
    if (gated.length && 'ResizeObserver' in window) {
        const byLayer = new Map()
        for (const el of gated) {
            const layer = el.closest('[data-parallax-layer]')
            const min = parseFloat(el.dataset.decoMinH)
            if (!layer || !Number.isFinite(min)) continue
            if (!byLayer.has(layer)) byLayer.set(layer, [])
            byLayer.get(layer).push({ el, min })
        }
        // Observing fires immediately per layer (initial state), then again on any
        // height change — image loads, accordions, Sprig swaps, viewport resize.
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const h = entry.contentRect.height
                for (const { el, min } of byLayer.get(entry.target) ?? []) {
                    el.classList.toggle('is-deco-visible', h >= min)
                }
            }
        })
        byLayer.forEach((_, layer) => ro.observe(layer))
    }
})()

// === Two-column rich text: keep headings with their content ===
// Multicol has no cross-browser "keep heading with next block" — Safari/Firefox
// ignore `break-after: avoid-column` inside columns — so group each h2 with its
// following element and let `break-inside: avoid` move the pair as one unit.
document.querySelectorAll('.rich-text--columns h2').forEach((h2) => {
    const next = h2.nextElementSibling
    if (!next) return
    const wrap = document.createElement('div')
    wrap.className = 'break-inside-avoid'
    h2.before(wrap)
    wrap.append(h2, next)
})

window.Alpine = Alpine
Alpine.plugin(collapse)

Alpine.data('selectProjectsCarousel', () => {
    let swiper = null
    let desktopMq = null
    let onBreakpointChange = null

    return {
        init() {
            if (!this.$refs.carousel) return

            const headingSlide = this.$refs.headingSlide
            const wrapper = this.$refs.carousel.querySelector('.swiper-wrapper')
            desktopMq = window.matchMedia('(min-width: 1024px)')

            // Desktop view = heading slide + 2 cards (3 uniform 1/3-width slides).
            const visibleCount = () => desktopMq.matches ? 3 : 1

            const updateOpacity = () => {
                if (!swiper) return
                const active = swiper.activeIndex
                const count = visibleCount()
                swiper.slides.forEach((slide, i) => {
                    slide.style.opacity = (i >= active && i < active + count) ? '1' : '0.25'
                })
            }

            // The heading rides inside the carousel on desktop only; Swiper has no
            // per-breakpoint way to skip a slide, so toggle it in the DOM.
            const syncHeadingSlide = () => {
                if (desktopMq.matches && !headingSlide.isConnected) {
                    wrapper.prepend(headingSlide)
                } else if (!desktopMq.matches && headingSlide.isConnected) {
                    headingSlide.remove()
                }
            }
            syncHeadingSlide()

            swiper = new Swiper(this.$refs.carousel, {
                modules: [Navigation, Scrollbar],
                // Slide widths + gaps are CSS-driven (src/css/vendor/swiper.css) so
                // the pre-init layout already matches — no reflow when Swiper mounts.
                slidesPerView: 'auto',
                navigation: {
                    prevEl: this.$refs.prevBtn,
                    nextEl: this.$refs.nextBtn,
                },
                scrollbar: {
                    el: this.$refs.scrollbar,
                    draggable: true,
                },
                on: {
                    slideChange: () => updateOpacity(),
                    resize: () => updateOpacity(),
                },
            })
            updateOpacity()

            onBreakpointChange = () => {
                syncHeadingSlide()
                swiper.update()
                updateOpacity()
            }
            desktopMq.addEventListener('change', onBreakpointChange)
        },
        destroy() {
            if (desktopMq && onBreakpointChange) {
                desktopMq.removeEventListener('change', onBreakpointChange)
                desktopMq = null
                onBreakpointChange = null
            }
            if (swiper) {
                swiper.destroy(true, true)
                swiper = null
            }
        },
    }
})

Alpine.data('imageGalleryCarousel', () => {
    let swiper = null

    return {
        currentSlide: 1,
        totalSlides: 0,

        init() {
            if (!this.$refs.gallery) return

            swiper = new Swiper(this.$refs.gallery, {
                modules: [Navigation],
                slidesPerView: 1,
                spaceBetween: 0,
                loop: true,
                navigation: {
                    prevEl: this.$refs.prevBtn,
                    nextEl: this.$refs.nextBtn,
                },
                on: {
                    init: (s) => {
                        this.totalSlides = s.slides.length
                        this.currentSlide = s.realIndex + 1
                    },
                    slideChange: (s) => {
                        this.currentSlide = s.realIndex + 1
                    },
                },
            })
        },
        destroy() {
            if (swiper) {
                swiper.destroy(true, true)
                swiper = null
            }
        },
    }
})

Alpine.data('projectMap', () => {
    let desktopMq = null
    let onBreakpointChange = null
    let placementObserver = null

    const EDGE_PAD = 8
    const COLLISION_PAD = 8

    // Dot stays fixed; box attaches at one corner or edge (8 orientations around the pin)
    const ALL_ANCHORS = [
        'anchor-tl',
        'anchor-tr',
        'anchor-bl',
        'anchor-br',
        'anchor-lc',
        'anchor-rc',
        'anchor-tc',
        'anchor-bc',
    ]

    const anchorOrder = (pin) => {
        const order = []

        if (pin.x <= 48) {
            order.push('anchor-tl', 'anchor-bl', 'anchor-lc', 'anchor-tc', 'anchor-bc')
        }
        if (pin.x >= 52) {
            order.push('anchor-tr', 'anchor-br', 'anchor-rc', 'anchor-tc', 'anchor-bc')
        }
        if (pin.y <= 48) {
            order.push('anchor-tl', 'anchor-tr', 'anchor-tc', 'anchor-lc', 'anchor-rc')
        }
        if (pin.y >= 52) {
            order.push('anchor-bl', 'anchor-br', 'anchor-bc', 'anchor-lc', 'anchor-rc')
        }

        for (const anchor of ALL_ANCHORS) {
            if (!order.includes(anchor)) order.push(anchor)
        }

        return order
    }

    const labelRect = (pin, anchor, w, h, cw, ch) => {
        const dotX = (pin.x / 100) * cw
        const dotY = (pin.y / 100) * ch
        let x
        let y

        switch (anchor) {
            case 'anchor-tr':
                x = dotX - w
                y = dotY
                break
            case 'anchor-bl':
                x = dotX
                y = dotY - h
                break
            case 'anchor-br':
                x = dotX - w
                y = dotY - h
                break
            case 'anchor-lc':
                x = dotX
                y = dotY - h / 2
                break
            case 'anchor-rc':
                x = dotX - w
                y = dotY - h / 2
                break
            case 'anchor-tc':
                x = dotX - w / 2
                y = dotY
                break
            case 'anchor-bc':
                x = dotX - w / 2
                y = dotY - h
                break
            default:
                x = dotX
                y = dotY
        }

        return { x, y, w, h, right: x + w, bottom: y + h }
    }

    const outOfBoundsPenalty = (rect, cw, ch) => {
        let penalty = 0
        if (rect.x < EDGE_PAD) penalty += EDGE_PAD - rect.x
        if (rect.y < EDGE_PAD) penalty += EDGE_PAD - rect.y
        if (rect.right > cw - EDGE_PAD) penalty += rect.right - (cw - EDGE_PAD)
        if (rect.bottom > ch - EDGE_PAD) penalty += rect.bottom - (ch - EDGE_PAD)
        return penalty * 200
    }

    const overlapArea = (a, b) => {
        const overlapX = Math.max(0, Math.min(a.right, b.right) - Math.max(a.x, b.x) - COLLISION_PAD)
        const overlapY = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.y, b.y) - COLLISION_PAD)
        return overlapX * overlapY
    }

    const placementScore = (rect, placedRects, cw, ch) => {
        let score = outOfBoundsPenalty(rect, cw, ch)
        for (const other of placedRects) {
            score += overlapArea(rect, other) * 3
        }
        return score
    }

    const neighborCount = (pin, pins) =>
        pins.filter((other) => {
            if (other.id === pin.id) return false
            return Math.hypot(other.x - pin.x, other.y - pin.y) < 18
        }).length

    const computePlacements = (pins, root, labelSizes) => {
        const canvas = root.querySelector('.project-map__canvas')
        if (!canvas || !pins.length) return {}

        const { width: cw, height: ch } = canvas.getBoundingClientRect()
        if (!cw || !ch) return {}

        const sorted = [...pins].sort((a, b) => neighborCount(b, pins) - neighborCount(a, pins))
        const placements = {}
        const placedRects = []

        for (const pin of sorted) {
            const fallback = { w: Math.min(cw * 0.42, 272), h: 44 }
            const size = labelSizes[pin.id] ?? fallback
            let bestAnchor = anchorOrder(pin)[0]
            let bestScore = Infinity

            for (const anchor of anchorOrder(pin)) {
                const rect = labelRect(pin, anchor, size.w, size.h, cw, ch)
                const score = placementScore(rect, placedRects, cw, ch)
                if (score < bestScore) {
                    bestScore = score
                    bestAnchor = anchor
                }
            }

            placements[pin.id] = bestAnchor
            placedRects.push(labelRect(pin, bestAnchor, size.w, size.h, cw, ch))
        }

        return placements
    }

    let layoutRaf = null
    let resizeDebounce = null

    return {
        pins: [],
        closedIds: [],
        activeId: null,
        isDesktop: false,
        measuring: false,
        placements: {},

        init() {
            const raw = this.$el.dataset.mapPins
            if (raw) {
                try {
                    this.pins = JSON.parse(raw)
                } catch {
                    this.pins = []
                }
            }

            desktopMq = window.matchMedia('(min-width: 1024px)')
            this.isDesktop = desktopMq.matches
            this.resetState()

            onBreakpointChange = () => {
                this.isDesktop = desktopMq.matches
                this.resetState()
                this.runPlacementLayout()
            }
            desktopMq.addEventListener('change', onBreakpointChange)

            this.$nextTick(() => this.runPlacementLayout())

            const canvas = this.$el.querySelector('.project-map__canvas')
            if (canvas && 'ResizeObserver' in window) {
                placementObserver = new ResizeObserver(() => {
                    clearTimeout(resizeDebounce)
                    resizeDebounce = setTimeout(() => this.runPlacementLayout(), 120)
                })
                placementObserver.observe(canvas)
            }

            if (canvas?.querySelector('img')) {
                canvas.querySelector('img').addEventListener('load', () => this.runPlacementLayout(), { once: true })
            }
        },

        destroy() {
            if (desktopMq && onBreakpointChange) {
                desktopMq.removeEventListener('change', onBreakpointChange)
                desktopMq = null
                onBreakpointChange = null
            }
            if (placementObserver) {
                placementObserver.disconnect()
                placementObserver = null
            }
            clearTimeout(resizeDebounce)
            if (layoutRaf) cancelAnimationFrame(layoutRaf)
        },

        resetState() {
            this.closedIds = []
            if (this.isDesktop) {
                this.activeId = null
            } else {
                this.activeId = this.pins[0]?.id ?? null
            }
        },

        measureLabels() {
            const canvas = this.$el.querySelector('.project-map__canvas')
            if (!canvas) return {}

            const sizes = {}
            for (const pin of this.pins) {
                const label = canvas.querySelector(`[data-pin-label="${pin.id}"]`)
                if (!label) continue

                const { width, height } = label.getBoundingClientRect()
                if (width > 0 && height > 0) {
                    sizes[pin.id] = { w: width, h: height }
                }
            }

            return sizes
        },

        applyPlacements(labelSizes) {
            this.placements = computePlacements(this.pins, this.$el, labelSizes)
        },

        layoutPass(pass = 0) {
            const labelSizes = this.measureLabels()
            this.applyPlacements(labelSizes)

            if (pass === 0) {
                this.$nextTick(() => {
                    layoutRaf = requestAnimationFrame(() => this.layoutPass(1))
                })
                return
            }

            const labelSizesAfter = this.measureLabels()
            this.applyPlacements(labelSizesAfter)
            this.measuring = false
        },

        runPlacementLayout() {
            if (layoutRaf) cancelAnimationFrame(layoutRaf)
            this.measuring = true
            this.$nextTick(() => {
                layoutRaf = requestAnimationFrame(() => this.layoutPass(0))
            })
        },

        pinPlacement(id) {
            const pin = this.pins.find((p) => p.id === id)
            return this.placements[id] ?? pin?.placement ?? 'anchor-tl'
        },

        isOpen(id) {
            if (this.closedIds.includes(id)) return false
            if (this.isDesktop) return true
            return this.activeId === id
        },

        pinState(id) {
            return this.isOpen(id) ? 'is-open' : 'is-closed'
        },

        togglePin(id) {
            if (this.isDesktop) {
                if (this.closedIds.includes(id)) {
                    this.closedIds = this.closedIds.filter((i) => i !== id)
                } else {
                    this.closedIds = [...this.closedIds, id]
                }
                return
            }

            if (this.activeId === id) {
                this.activeId = null
                this.closedIds = [...this.closedIds, id]
                return
            }

            this.closedIds = this.closedIds.filter((i) => i !== id)
            this.activeId = id
        },
    }
})

Alpine.data('heroFadeCarousel', () => {
    let swiper = null
    let loadHandler = null
    let loadTimer = null

    return {
        init() {
            if (!this.$refs.hero) return

            const hero = this.$refs.hero
            const slideCount = hero.querySelectorAll('.swiper-slide').length

            // Static hero when there's nothing to cycle, or the user prefers reduced
            // motion — uninitialised Swiper markup shows the first slide only.
            if (slideCount < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

            const hydrateSlides = () => {
                hero.querySelectorAll('template[data-hero-slide-image]').forEach((template) => {
                    template.replaceWith(template.content.cloneNode(true))
                })
            }

            const initSwiper = () => {
                if (swiper) return

                hydrateSlides()

                swiper = new Swiper(hero, {
                    modules: [EffectFade, Autoplay],
                    effect: 'fade',
                    fadeEffect: { crossFade: true },
                    speed: 1000,
                    rewind: true,
                    allowTouchMove: false,
                    autoplay: {
                        delay: 4000,
                        disableOnInteraction: false,
                    },
                })
            }

            loadHandler = initSwiper

            if (document.readyState === 'complete') {
                loadTimer = window.setTimeout(initSwiper, 0)
            } else {
                window.addEventListener('load', loadHandler, { once: true })
            }
        },
        destroy() {
            if (loadHandler) {
                window.removeEventListener('load', loadHandler)
                loadHandler = null
            }

            if (loadTimer) {
                window.clearTimeout(loadTimer)
                loadTimer = null
            }

            if (swiper) {
                swiper.destroy(true, true)
                swiper = null
            }
        },
    }
})

Alpine.start()

CookieConsent.run({
    guiOptions: {
        consentModal: {
            layout: 'bar',
            position: 'bottom',
            equalWeightButtons: true,
            flipButtons: false,
        },
    },
    categories: {
        necessary: {
            enabled: true,
            readOnly: true,
        },
        analytics: {},
    },
    language: {
        default: 'en',
        translations: {
            en: {
                consentModal: {
                    title: cookieBannerTitle(),
                    description: cookieBannerDescription(),
                    acceptAllBtn: 'Accept',
                    acceptNecessaryBtn: 'Reject',
                },
            },
        },
    },
})

function cookieBannerTitle() {
    return window.SBF_COOKIE_BANNER?.title || 'We Value Your Privacy'
}

function cookieBannerDescription() {
    return window.SBF_COOKIE_BANNER?.description || 'This website uses cookies to improve your browsing experience. By using or registering on any portion of this site, you agree to our privacy and cookie statement.'
}

window.exampleAlert = () => alert('DDEV + Vite + Tailwind + Alpine are wired up!')
